import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCallSocket, destroyCallSocket, releaseCallLock, getCallLock, TAB_ID } from "../services/callSocket";
import { callActions } from "../../../../core/data/redux/live-call/slice";
import type { AppDispatch } from "../../../../core/data/redux/store";

interface Props { children: React.ReactNode; }

export default function CallSessionProvider({ children }: Props) {
    const dispatch = useDispatch<AppDispatch>();
    const user = useSelector((s: any) => s.user.user);
    const callState = useSelector((s: any) => s.liveCall);

    const activeCallRef = useRef<any>(null);
    const isAutoCallRef = useRef(false);
    const isLeadsModeRef = useRef(false);
    const manualEndRef = useRef(false);
    const prevActiveCallRef = useRef<any>(null);

    useEffect(() => { activeCallRef.current = callState.activeCall; }, [callState.activeCall]);
    useEffect(() => { isAutoCallRef.current = callState.isAutoCall; }, [callState.isAutoCall]);
    useEffect(() => { isLeadsModeRef.current = callState.isLeadsMode; }, [callState.isLeadsMode]);

    useEffect(() => {
        if (prevActiveCallRef.current && !callState.activeCall) {
            releaseCallLock();
        }
        prevActiveCallRef.current = callState.activeCall;
    }, [callState.activeCall]);

    useEffect(() => {
        const handlePageHide = () => releaseCallLock();
        window.addEventListener("pagehide", handlePageHide);
        return () => window.removeEventListener("pagehide", handlePageHide);
    }, []);

    useEffect(() => {
        const socket = getCallSocket();
        const userId = user?.userDetails?.id;
        socket.connect();

        socket.on("connect", () => {
            socket.emit("register", { userId, type: "web" });
        });

        socket.on("callConnected", () => {
            if (!activeCallRef.current) return;
            dispatch(callActions.setConnected(true));
        });

        socket.on("callDisconnected", () => {
            if (!activeCallRef.current) return;

            if (callState.showLeadMgmtOutcomeModal) return;

            if (manualEndRef.current) {
                manualEndRef.current = false;
                return;
            }

            const snapshot = activeCallRef.current;
            dispatch(callActions.setConnected(false));
            dispatch(callActions.setActiveCall(null));
            dispatch(callActions.setCallDisconnectedData(snapshot));

            if (isLeadsModeRef.current) {
                dispatch(callActions.setShowLeadMgmtOutcomeModal(true));
            } else {
                dispatch(callActions.setShowReferencePopup(true));
            }
        });

        socket.on("callRejected", () => {
            if (!activeCallRef.current) return;

            dispatch(callActions.setRejectedMessage("Call rejected by client"));
            setTimeout(() => dispatch(callActions.setRejectedMessage("")), 2000);

            const snapshot = activeCallRef.current;
            dispatch(callActions.setConnected(false));
            dispatch(callActions.setActiveCall(null));
            dispatch(callActions.setCallDisconnectedData(snapshot));

            if (!isAutoCallRef.current) {
                if (isLeadsModeRef.current) {
                    dispatch(callActions.setShowLeadMgmtOutcomeModal(true));
                } else {
                    dispatch(callActions.setShowReferencePopup(true));
                }
            }
        });

        return () => { destroyCallSocket(); };
    }, []);

    return <>{children}</>;
}
